import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BODY_REGIONS,
  EQUIPMENT,
  MOVEMENT_PATTERNS,
  MUSCLES,
  TRACKING_MODES,
  isSafeVideoUrl,
  setFieldsFor,
  videoHost,
} from '@kraftwerk/shared';
import type {
  BodyRegion,
  Equipment,
  ExerciseVideo,
  MovementPattern,
  Muscle,
  TrackingMode,
} from '@kraftwerk/shared';
import type { LocalExercise } from '../../db/local.js';

export interface EditorDraft {
  name: string;
  variantLabel: string | null;
  trackingMode: TrackingMode;
  equipment: Equipment;
  movementPattern: MovementPattern;
  bodyRegion: BodyRegion;
  primaryMuscles: Muscle[];
  secondaryMuscles: Muscle[];
  isCompound: boolean;
  isUnilateral: boolean;
  defaultSets: number;
  repMin: number;
  repMax: number;
  incrementKg: number;
  restSeconds: number;
  cues: string[];
  videos: ExerciseVideo[];
  setupNotes: string | null;
  note: string | null;
  isFavorite: boolean;
}

function toDraft(base: LocalExercise | undefined, variant: boolean): EditorDraft {
  return {
    name: variant ? '' : (base?.name ?? ''),
    variantLabel: variant ? '' : (base?.variantLabel ?? null),
    trackingMode: base?.trackingMode ?? 'weightReps',
    equipment: base?.equipment ?? 'other',
    movementPattern: base?.movementPattern ?? 'isolation',
    bodyRegion: base?.bodyRegion ?? 'fullBody',
    primaryMuscles: base ? [...base.primaryMuscles] : [],
    secondaryMuscles: base ? [...base.secondaryMuscles] : [],
    isCompound: base?.isCompound ?? false,
    isUnilateral: base?.isUnilateral ?? false,
    defaultSets: base?.defaultSets ?? 3,
    repMin: base?.repMin ?? 8,
    repMax: base?.repMax ?? 12,
    incrementKg: base?.incrementKg ?? 2.5,
    restSeconds: base?.restSeconds ?? 90,
    // Bei einer neuen Variante bleiben Hinweise und Videos leer: Die Oberfläche
    // leiht sie sichtbar vom Grundtyp, statt sie stumm zu kopieren.
    cues: variant ? [] : [...(base?.cues ?? [])],
    videos: variant ? [] : [...(base?.videos ?? [])],
    setupNotes: variant ? null : (base?.setupNotes ?? null),
    note: variant ? null : (base?.note ?? null),
    isFavorite: variant ? false : (base?.isFavorite ?? false),
  };
}

/**
 * Der Editor — für neue Übungen, Varianten und Bearbeitungen dieselbe Maske.
 *
 * Bewusst ein langes Formular statt eines Assistenten: Wer eine Übung anlegt,
 * hat das Gerät vor sich und will alles auf einmal eintragen, nicht durch fünf
 * Schritte klicken.
 */
export function ExerciseEditor({
  base,
  variant,
  existing,
  onSave,
  onCancel,
  onDelete,
}: {
  base?: LocalExercise;
  variant?: boolean;
  existing?: boolean;
  onSave: (draft: EditorDraft) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<EditorDraft>(() => toDraft(base, variant ?? false));
  const [videoError, setVideoError] = useState<string | null>(null);
  const fields = setFieldsFor(draft.trackingMode);
  const ids = {
    name: useId(),
    label: useId(),
    mode: useId(),
    equipment: useId(),
    pattern: useId(),
    region: useId(),
    sets: useId(),
    repMin: useId(),
    repMax: useId(),
    increment: useId(),
    rest: useId(),
    cues: useId(),
    setup: useId(),
    note: useId(),
  };

  const set = <K extends keyof EditorDraft>(key: K, value: EditorDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <form
      className="flex flex-col gap-3 rounded-xl border border-accent bg-surface-raised p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (draft.name.trim() === '') return;
        void onSave({ ...draft, name: draft.name.trim() });
      }}
    >
      {variant ? (
        <p className="text-sm text-ink-muted">
          {t('training.library.variantOf', { name: base?.name ?? '' })}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-ink-muted">
          {t('training.exercise.name')}
          <input
            id={ids.name}
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            required
            maxLength={120}
            className="kw-field text-ink"
          />
        </label>

        <label className="flex w-40 flex-col gap-1 text-xs text-ink-muted">
          {t('training.exercise.variantLabel')}
          <input
            id={ids.label}
            value={draft.variantLabel ?? ''}
            onChange={(event) => set('variantLabel', event.target.value || null)}
            maxLength={60}
            className="kw-field text-ink"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select
          id={ids.mode}
          label={t('training.exercise.trackingMode')}
          value={draft.trackingMode}
          options={TRACKING_MODES.map((option) => ({
            value: option,
            label: t(`training.mode.${option}`),
          }))}
          onChange={(value) => set('trackingMode', value as TrackingMode)}
        />
        <Select
          id={ids.equipment}
          label={t('training.exercise.equipment')}
          value={draft.equipment}
          options={EQUIPMENT.map((option) => ({
            value: option,
            label: t(`training.equipment.${option}`),
          }))}
          onChange={(value) => set('equipment', value as Equipment)}
        />
        <Select
          id={ids.pattern}
          label={t('training.exercise.movementPattern')}
          value={draft.movementPattern}
          options={MOVEMENT_PATTERNS.map((option) => ({
            value: option,
            label: t(`training.pattern.${option}`),
          }))}
          onChange={(value) => set('movementPattern', value as MovementPattern)}
        />
        <Select
          id={ids.region}
          label={t('training.exercise.bodyRegion')}
          value={draft.bodyRegion}
          options={BODY_REGIONS.map((option) => ({
            value: option,
            label: t(`training.region.${option}`),
          }))}
          onChange={(value) => set('bodyRegion', value as BodyRegion)}
        />
      </div>

      <MusclePicker
        legend={t('training.exercise.primaryMuscles')}
        selected={draft.primaryMuscles}
        onToggle={(muscle) =>
          set(
            'primaryMuscles',
            draft.primaryMuscles.includes(muscle)
              ? draft.primaryMuscles.filter((entry) => entry !== muscle)
              : [...draft.primaryMuscles, muscle],
          )
        }
      />
      <MusclePicker
        legend={t('training.exercise.secondaryMuscles')}
        selected={draft.secondaryMuscles}
        onToggle={(muscle) =>
          set(
            'secondaryMuscles',
            draft.secondaryMuscles.includes(muscle)
              ? draft.secondaryMuscles.filter((entry) => entry !== muscle)
              : [...draft.secondaryMuscles, muscle],
          )
        }
      />

      <div className="flex flex-wrap gap-3">
        <NumberField
          id={ids.sets}
          label={t('training.exercise.defaultSets')}
          value={draft.defaultSets}
          onChange={(value) => set('defaultSets', value)}
        />
        {fields.reps ? (
          <>
            <NumberField
              id={ids.repMin}
              label={t('training.exercise.repMin')}
              value={draft.repMin}
              onChange={(value) => set('repMin', value)}
            />
            <NumberField
              id={ids.repMax}
              label={t('training.exercise.repMax')}
              value={draft.repMax}
              onChange={(value) => set('repMax', value)}
            />
          </>
        ) : null}
        <NumberField
          id={ids.increment}
          label={t('training.exercise.incrementKg')}
          value={draft.incrementKg}
          step={0.5}
          onChange={(value) => set('incrementKg', value)}
        />
        <NumberField
          id={ids.rest}
          label={t('training.exercise.restSeconds')}
          value={draft.restSeconds}
          step={15}
          onChange={(value) => set('restSeconds', value)}
        />
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.isCompound}
            onChange={(event) => set('isCompound', event.target.checked)}
            className="size-4"
          />
          {t('training.exercise.isCompound')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.isUnilateral}
            onChange={(event) => set('isUnilateral', event.target.checked)}
            className="size-4"
          />
          {t('training.exercise.isUnilateral')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.isFavorite}
            onChange={(event) => set('isFavorite', event.target.checked)}
            className="size-4"
          />
          {t('training.exercise.isFavorite')}
        </label>
      </div>

      <label className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('training.exercise.cues')}
        <textarea
          id={ids.cues}
          value={draft.cues.join('\n')}
          onChange={(event) =>
            set(
              'cues',
              event.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line.length > 0),
            )
          }
          rows={3}
          className="kw-field text-ink"
        />
        <span>{t('training.exercise.cuesHint')}</span>
      </label>

      <VideoEditor
        videos={draft.videos}
        error={videoError}
        onError={setVideoError}
        onChange={(videos) => set('videos', videos)}
      />

      <label className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('training.exercise.setupNotes')}
        <input
          id={ids.setup}
          value={draft.setupNotes ?? ''}
          onChange={(event) => set('setupNotes', event.target.value || null)}
          maxLength={500}
          placeholder={t('training.exercise.setupPlaceholder')}
          className="kw-field text-ink"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('training.exercise.note')}
        <textarea
          id={ids.note}
          value={draft.note ?? ''}
          onChange={(event) => set('note', event.target.value || null)}
          rows={2}
          className="kw-field text-ink"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className="kw-btn kw-btn-small">
          {t('training.library.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-line px-3 py-2 text-sm text-ink-muted hover:border-accent"
        >
          {t('training.library.cancel')}
        </button>
        {existing && onDelete ? (
          <button
            type="button"
            onClick={() => void onDelete()}
            className="ml-auto rounded-lg border border-line px-3 py-2 text-sm text-ink-muted hover:border-danger hover:text-danger"
          >
            {t('training.library.delete')}
          </button>
        ) : null}
      </div>
      {existing ? (
        <p className="text-xs text-ink-muted">{t('training.library.deleteHint')}</p>
      ) : null}
    </form>
  );
}

function VideoEditor({
  videos,
  error,
  onError,
  onChange,
}: {
  videos: ExerciseVideo[];
  error: string | null;
  onError: (message: string | null) => void;
  onChange: (videos: ExerciseVideo[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [start, setStart] = useState('');

  return (
    <fieldset className="flex flex-col gap-2 rounded-lg border border-line p-3">
      <legend className="px-1 text-xs text-ink-muted">{t('training.exercise.videos')}</legend>

      {videos.length === 0 ? (
        <p className="text-xs text-ink-muted">{t('training.exercise.noVideo')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {videos.map((video) => (
            <li key={video.url} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">
                {video.label} ·{' '}
                <span className="text-ink-muted">
                  {videoHost(video.url) ?? t('training.exercise.unknownHost')}
                </span>
                {video.startSeconds ? (
                  <span className="text-ink-muted"> · {video.startSeconds} s</span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={() => onChange(videos.filter((entry) => entry.url !== video.url))}
                aria-label={t('training.exercise.removeVideoNamed', { label: video.label })}
                className="kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
              >
                {t('training.library.delete')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex w-32 flex-col gap-1 text-xs text-ink-muted">
          {t('training.exercise.videoLabel')}
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-ink-muted">
          {t('training.exercise.videoUrl')}
          <input
            value={url}
            inputMode="url"
            placeholder="https://…"
            onChange={(event) => setUrl(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
        <label className="flex w-24 flex-col gap-1 text-xs text-ink-muted">
          {t('training.exercise.videoStart')}
          <input
            value={start}
            inputMode="numeric"
            onChange={(event) => setStart(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
        <button
          type="button"
          onClick={() => {
            const trimmedUrl = url.trim();
            const trimmedLabel = label.trim() || t('training.exercise.videoDefaultLabel');
            // Nur https — und der Gastgeber steht später am Knopf, damit man
            // sieht, wohin man geschickt wird.
            if (!isSafeVideoUrl(trimmedUrl)) {
              onError(t('training.exercise.videoInvalid'));
              return;
            }
            onError(null);
            const seconds = Number(start.trim());
            onChange([
              ...videos,
              {
                label: trimmedLabel,
                url: trimmedUrl,
                startSeconds: Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : null,
              },
            ]);
            setLabel('');
            setUrl('');
            setStart('');
          }}
          className="rounded-lg border border-line px-3 py-2 text-sm hover:border-accent"
        >
          {t('training.exercise.addVideo')}
        </button>
      </div>

      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      <p className="text-xs text-ink-muted">{t('training.exercise.videoPrivacy')}</p>
    </fieldset>
  );
}

function MusclePicker({
  legend,
  selected,
  onToggle,
}: {
  legend: string;
  selected: Muscle[];
  onToggle: (muscle: Muscle) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs text-ink-muted">{legend}</legend>
      <div className="flex flex-wrap gap-1">
        {MUSCLES.map((muscle) => {
          const active = selected.includes(muscle);
          return (
            <button
              key={muscle}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(muscle)}
              className="kw-toggle"
            >
              {t(`training.muscle.${muscle}`)}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function Select({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label htmlFor={id} className="flex min-w-36 flex-1 flex-col gap-1 text-xs text-ink-muted">
      {label}
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="kw-field text-ink"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberField({
  id,
  label,
  value,
  step,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  step?: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label htmlFor={id} className="flex w-24 flex-col gap-1 text-xs text-ink-muted">
      {label}
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step={step ?? 1}
        min={0}
        value={value}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
        className="kw-field text-ink"
      />
    </label>
  );
}
